import { FC, useRef, useState } from 'react';
import { SelectedRecipientSidebarCard } from './SelectedRecipientSidebarCard';
import { HeaderBar } from './HeaderBar';
import { IRecipient } from 'store/recipients/types';
import classNames from 'classnames';

interface Props {
  items: IRecipient[];
  onRemove: (index: number) => void;
  primaryColor: string;
  secondaryColor: string;
  onDonate?: () => void;
  isLoading?: boolean;
}
const minVh = 28;
const maxVh = 85;
const defaultOpenVh = 72;
const DRAG_THRESHOLD_PX = 8;

export const BottomCart: FC<Props> = ({
  items,
  onRemove,
  primaryColor,
  secondaryColor,
  onDonate,
  isLoading,
}) => {
  const [open, setOpen] = useState(false);
  const [heightVh, setHeightVh] = useState(minVh);
  const dragMovedRef = useRef(false);

  const openSheet = () => {
    setOpen(true);
    setHeightVh(current => (current < defaultOpenVh ? defaultOpenVh : current));
  };

  // Drag-to-resize handlers
  const startDrag = (startY: number) => {
    dragMovedRef.current = false;
    const startHeightPx = (heightVh / 100) * window.innerHeight;
    const onMove = (e: PointerEvent | TouchEvent | MouseEvent) => {
      const clientY =
        (e as PointerEvent).clientY ||
        (e as TouchEvent).touches?.[0]?.clientY ||
        (e as MouseEvent).clientY ||
        0;
      const delta = startY - clientY; // dragging up increases height
      if (Math.abs(delta) > DRAG_THRESHOLD_PX) {
        dragMovedRef.current = true;
      }
      const newHeightPx = Math.max(0, Math.min(window.innerHeight, startHeightPx + delta));
      const newVh = (newHeightPx / window.innerHeight) * 100;
      const clamped = Math.max(minVh, Math.min(maxVh, newVh));
      setHeightVh(clamped);
    };
    const stop = () => {
      window.removeEventListener('pointermove', onMove as any);
      window.removeEventListener('mousemove', onMove as any);
      window.removeEventListener('touchmove', onMove as any);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('mouseup', stop);
      window.removeEventListener('touchend', stop);
    };
    window.addEventListener('pointermove', onMove as any);
    window.addEventListener('mousemove', onMove as any);
    window.addEventListener('touchmove', onMove as any, { passive: false });
    window.addEventListener('pointerup', stop);
    window.addEventListener('mouseup', stop);
    window.addEventListener('touchend', stop);
  };

  const onHandlePointerDown: React.PointerEventHandler<HTMLDivElement> = e => {
    e.preventDefault();
    if (!open) openSheet();
    startDrag(e.clientY);
  };
  const onHandleTouchStart: React.TouchEventHandler<HTMLDivElement> = e => {
    const y = e.touches?.[0]?.clientY || 0;
    if (!open) openSheet();
    startDrag(y);
  };

  const onHeaderClickWhenOpen = () => {
    if (dragMovedRef.current) return;
    setOpen(false);
  };

  return (
    <div className="bottom-cart-container">
      {!open && <HeaderBar isOpen={open} onClick={openSheet} items={items} />}
      <div className={classNames('mobile-bottom-sheet', { 'is-open': open })}>
        <div className="sheet-overlay" onClick={() => setOpen(false)} />
        <div className="sheet-panel" style={{ height: `${heightVh}vh`, maxHeight: `${maxVh}vh` }}>
          <div className="sheet-header">
            <div
              className="drag-handle"
              onPointerDown={onHandlePointerDown}
              onTouchStart={onHandleTouchStart}
            />
            <HeaderBar
              isOpen={open}
              onClick={onHeaderClickWhenOpen}
              onPointerDown={onHandlePointerDown}
              onTouchStart={onHandleTouchStart}
              items={items}
            />
          </div>
          <div className="sheet-content">
            <SelectedRecipientSidebarCard
              recipients={items}
              onRemove={onRemove}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              onDonate={onDonate}
              donatePosition="top"
              hideHeader
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
